/*
 * Copyright (c) 2025 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import {StringParam, useQueryParam} from "use-query-params";
import * as React from "react";
import {useEffect} from "react";
import {useGetCurrentPageUserAccessLevel} from "../../../../../redux/ducks/AccessControl/accessControlHelpers";
import {EUserPermissionAccess} from "../../../../../redux/ducks/AccessControl/types";
import {cloneDeep} from "lodash";
import {TreeItem} from "@mui/x-tree-view/TreeItem";
import {ITreeNode} from "../../../CoverageRequirements/ItemDetails/Tabs/CoverageRequirementsTab/LeftPanelComponents/TreeComponent/CoverageRequirementsTreeComponent";
import ArgosFlexbox from "../../../../components/Containers/ArgosFlexbox";
import {Typography} from "@mui/material";
import Loader from "../../../../components/Loader/Loader";
import CheckboxDraggableTree from "../../../../components/Tree/CheckboxDraggableTree";
import ContextMenu from "./ContextMenu";
import AddAdasProgramDialog from "./AddAdasProgramDialog";
import { selectVehicleProgramsMainData } from "../../../../../redux/ducks/VehicleProgramsDetails/slice";
import {useAppSelector} from "../../../../../redux/hooks";

const VehicleProgramsTree = (props: any) => {
  const {developmentStagesTree, nodeDataLoading} = props
  const [developmentStageId, setDevelopmentStageId] = useQueryParam('developmentStageId', StringParam)
  const [adasProgramId, setAdasProgramId] = useQueryParam('adasProgramId', StringParam)

  const [expandedTreeNodes, setExpandedTreeNodes] = React.useState<string[]>(['root'])
  const [selectedNode, setSelectedNode] = React.useState<any>(null)

  /**
   * Access control
   */
  const [isUserAdmin, userAccessLevel] = useGetCurrentPageUserAccessLevel()
  const isProgramArchive = useAppSelector(state => selectVehicleProgramsMainData(state)?.isProgramArchive)
  const isReadOnly = userAccessLevel ===  EUserPermissionAccess.READ_ONLY && !isUserAdmin

  /**
   * Filter tree to remove adasProgram nodes if user is read only
   */
  const [filteredTree, setFilteredTree] = React.useState<any>(null)
  useEffect(() => {
    if (!developmentStagesTree) return
    let clonedTree = cloneDeep(developmentStagesTree)
    if (isReadOnly || isProgramArchive) {
      removeAdasProgramNodes(clonedTree, isReadOnly)
    }
    setFilteredTree(clonedTree)
  }, [developmentStagesTree, isReadOnly, isProgramArchive]);

  useEffect(() => {
    // get path to node with developmentStageId, and add all nodes to expandedTreeNodes
    // recursively loop through json tree and childdren to find path to node with developmentStageId
    const findNode = (node: any, path: any, _developmentStageId: string, _selectedNode: any) => {
      if (!node) return
      if (node?.developmentStageId == _developmentStageId) {
        // found node
        // setExpandedTreeNodes(path)
        // setSelectedNode(node)
        _selectedNode['data'] = node
        return
      }
      if (node?.children) {
        path.push(node?.nodeId?.toString())
        node?.children?.forEach((child: any) => {
          findNode(child, path, _developmentStageId, _selectedNode)
        })
      }
    }
    const path: any = []
    const _selectedNode = {}
    findNode(developmentStagesTree, path, developmentStageId ?? '', _selectedNode)
    // add nodes that are in path but not in expandedTreeNodes
    const newExpandedTreeNodes = [...expandedTreeNodes]
    path.forEach((nodeId: any) => {
      if (!newExpandedTreeNodes.includes(nodeId)) {
        newExpandedTreeNodes.push(nodeId)
      }
    })
    setExpandedTreeNodes(newExpandedTreeNodes)
    setSelectedNode(_selectedNode)
  }, [developmentStagesTree, developmentStageId]);

  /**
   * Context menu
   */
  const [isRightClickMenuOpen, setIsRightClickMenuOpen] = React.useState(false)
  const [menuPosition, setMenuPosition] = React.useState<any>(null)
  const [rightClickNodeData, setRightClickNodeData] = React.useState<any>(null)
  const treeNodeOnContextMenu = (e: any, node: any) => {
    e.stopPropagation();
    e.preventDefault();
    if (node?.nodeId === 'root') return
    setIsRightClickMenuOpen(true);
    setMenuPosition({
      x: e.pageX,
      y: e.pageY
    })
    setRightClickNodeData(node)
  }

  const renderTree = (nodes: any) => {
    if (!nodes) return null
    const labelText = nodes?.name
    const labelContent = (
      <div style={{display: 'flex', height: 42, alignItems: 'center'}}>{labelText}</div>
    )
    return (
      <TreeItem
        key={nodes.nodeId}
        itemId={nodes.nodeId?.toString()}
        label={labelContent}
        //   onLabelClick={(event) => {
        //   event.preventDefault(); // Prevent the default expansion behavior
        // }}
        onContextMenu={(e) => {
          treeNodeOnContextMenu(e, nodes)
        }}
        onClick={(e) => {
          // e.preventDefault()
          // Add development stage id to url
          const _developmentStageId = nodes.developmentStageId
          if (_developmentStageId) { // Without this check, setting would remove param from url (because setting to undefined)
            setDevelopmentStageId(_developmentStageId)
          }
          setSelectedNode(nodes)
        }}
      >
        {Array.isArray(nodes.children)
          ? nodes.children.map((node: any) => renderTree(node))
          : null}
      </TreeItem>
    )
  }


  const treeNodeOnClick = (_e: any, nodes: any, treeType: string, _treeData: any) => {
    if (nodes.type === 'devStageFolder') {
      setDevelopmentStageId(nodes.developmentStageId)
      setAdasProgramId(undefined)
    } else {
      setDevelopmentStageId(nodes.developmentStageId)
      setAdasProgramId(nodes.adasProgramId)
    }
    // if (nodes?.data?.developmentStageId) {
    //   setDevelopmentStageId(nodes.data.developmentStageId)
    // }
    // setSelectedNode(nodes)
  }

  function renderTreeItemLabel(nodes: ITreeNode) {

    const boxHeight = 40
    return (
      <ArgosFlexbox style={{height: boxHeight, alignItems: "center", width: "100%"}}>
        {/*<ArgosStudyIcon/>*/}
        <Typography style={{width: "100%", fontWeight: "normal"}}>
          {nodes.name}
        </Typography>
      </ArgosFlexbox>)
  }

  const [addAdasProgramDialogData, setAddAdasProgramDialogData] = React.useState<any>(null)

  return (
    <>
      <div style={{width: "100%", height: "100%", background: '#ffffff'}}>
        {
          developmentStagesTree && filteredTree &&
          <Loader isLoading={nodeDataLoading}>
            <CheckboxDraggableTree
              treeType={''}
              treeData={{
                tree: filteredTree,
              }}
              treeNodeOnClick={treeNodeOnClick}
              treeNodeOnContextMenu={treeNodeOnContextMenu}
              renderTreeItemLabel={renderTreeItemLabel}
              isMergeDialogUp={false}
              checkIfMerging={() => false}
              isDeleting={false}
              hideCheckboxes={true}
              disableDragAndDrop={true}
            />
          </Loader>
        }
      </div>

      {/* Context Menu */}
      {!isReadOnly && !isProgramArchive &&
        <ContextMenu
          addAdasProgramDialogData={addAdasProgramDialogData} setAddAdasProgramDialogData={setAddAdasProgramDialogData}
          isRightClickMenuOpen={isRightClickMenuOpen} setIsRightClickMenuOpen={setIsRightClickMenuOpen}
                   menuPosition={menuPosition} rightClickNodeData={rightClickNodeData}/>
      }

      <AddAdasProgramDialog
            open={addAdasProgramDialogData}
            setIsRightClickMenuOpen={setIsRightClickMenuOpen}
            onClose={() => setAddAdasProgramDialogData(null)}
            data={{
              rightClickNodeData
            }}
          />
    </>
  )
}

export default VehicleProgramsTree

const removeAdasProgramNodes = (node: any, isReadOnly: boolean): any => {
  if (isReadOnly && node.type === 'adasProgram') {
    return null;
  }

  if (node.children) {
    node.children = node.children
      .map((child: any) => removeAdasProgramNodes(child, isReadOnly))
      .filter((child: any) => child !== null);
  }

  return node;
};
const removeAdasProgramNodesWithoutModifyingOriginal = (originalTree: any, isReadOnly: boolean): any => {
  const treeCopy = cloneDeep(originalTree);
  return removeAdasProgramNodes(treeCopy, isReadOnly);
};
